import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/interactives/Popover.jsx'
import { faChevronDown, faDeleteLeft } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { RunnerState } from '@rundown-studio/types'
import { cn } from '@/utils'

type Operation = 'add' | 'subtract'

/** Default duration for the main button */
const DURATION_DEFAULT = 60_000

/** Max digits the calculator accepts (HHMMSS) */
const MAX_DIGITS = 6

/** Quick-adjust presets shown above the numpad. Each fires immediately on click. */
const QUICK_PRESETS: ReadonlyArray<{ label: string; ms: number }> = [
  { label: '10s', ms: 10_000 },
  { label: '30s', ms: 30_000 },
  { label: '1m', ms: 60_000 },
  { label: '5m', ms: 300_000 },
  { label: '10m', ms: 600_000 },
]

/** Returns a positively/negatively signed number for a given operation */
function getSignedDuration(op: Operation, num: number) {
  return op === 'add' ? Math.abs(num) : -Math.abs(num)
}

/** Convert a digit string ("130" → 90s, "10000" → 1h) into milliseconds.
 *  Digits fill HHMMSS from the right: last two = seconds, next two = minutes, rest = hours. */
function digitsToMs(digits: string): number {
  const padded = digits.padStart(6, '0').slice(-6)
  const hours = parseInt(padded.slice(0, 2), 10)
  const minutes = parseInt(padded.slice(2, 4), 10)
  const seconds = parseInt(padded.slice(4, 6), 10)
  return hours * 3_600_000 + minutes * 60_000 + seconds * 1_000
}

/** Format the digit buffer as HH:MM:SS for display. */
function formatDigits(digits: string): string {
  const padded = digits.padStart(6, '0').slice(-6)
  return `${padded.slice(0, 2)}:${padded.slice(2, 4)}:${padded.slice(4, 6)}`
}

type Props = {
  operation: Operation
  readonly: boolean
  loading: boolean
  runnerState: RunnerState
  isShowCaller: boolean
  onTimeOperation: (amount: number) => void
}

export function TransportTimeOperation({
  operation,
  readonly,
  loading,
  runnerState,
  isShowCaller,
  onTimeOperation,
}: Props) {
  const operator = operation === 'add' ? '+' : '-'
  const operationVerb = operation === 'add' ? 'Extend' : 'Shorten'

  const defaultDuration = getSignedDuration(operation, DURATION_DEFAULT)

  const isDisabled =
    readonly || runnerState !== RunnerState.ONAIR || !isShowCaller

  const [open, setOpen] = useState(false)
  const [digits, setDigits] = useState('')

  const displayMs = useMemo(() => digitsToMs(digits), [digits])
  const display = useMemo(() => formatDigits(digits), [digits])

  const pushDigit = useCallback((d: string) => {
    setDigits((prev) => {
      const next = (prev + d).replace(/^0+/, '')
      return next.slice(-MAX_DIGITS)
    })
  }, [])

  const backspace = useCallback(() => {
    setDigits((prev) => prev.slice(0, -1))
  }, [])

  const clear = useCallback(() => setDigits(''), [])

  const handleSave = useCallback(() => {
    if (displayMs <= 0) return
    onTimeOperation(getSignedDuration(operation, displayMs))
    setDigits('')
    setOpen(false)
  }, [displayMs, onTimeOperation, operation])

  const handleCancel = useCallback(() => {
    setDigits('')
    setOpen(false)
  }, [])

  const handleQuick = useCallback((ms: number) => {
    onTimeOperation(getSignedDuration(operation, ms))
    setDigits('')
    setOpen(false)
  }, [onTimeOperation, operation])

  return (
    <div className="flex">
      {operation === 'add' && (
        <Button
          className="text-base rounded-r-none"
          wrapperClass="flex-none"
          text={`${operator}1m`}
          colour="dark"
          toolTip={`${operationVerb} by 1 minute`}
          loading={loading}
          disabled={isDisabled}
          onClick={() => onTimeOperation(defaultDuration)}
        />
      )}
      <Popover
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onOpenChange={setOpen}
        placement={operation === 'add' ? 'bottom-end' : 'bottom-start'}
      >
        <PopoverTrigger asChild={true} disabled={isDisabled || loading}>
          <button
            type="button"
            disabled={isDisabled || loading}
            aria-label={`${operationVerb} by custom amount`}
            onClick={() => setOpen((v) => !v)}
            className={cn(
              'flex items-center justify-center h-9 w-9 p-0 rounded-sm focus:z-10',
              operation === 'add'
                ? 'rounded-l-none border-l-2 border-gray-800'
                : 'rounded-r-none border-r-2 border-gray-800',
              'bg-gray-900 hover:enabled:bg-gray-800 text-white text-sm font-semibold',
              'disabled:opacity-50 disabled:cursor-default',
              'focus:outline-hidden focus:ring-3 focus:ring-offset-1 focus:ring-offset-transparent',
              loading && 'bg-animated from-gray-800 to-gray-600',
            )}
          >
            <FontAwesomeIcon icon={faChevronDown} size="sm" fixedWidth />
          </button>
        </PopoverTrigger>
        <PopoverContent
          onSave={handleSave}
          onCancel={handleCancel}
          saveText={operationVerb}
          saveDisabled={displayMs <= 0}
        >
          <TimeCalculator
            label={`${operationVerb} by`}
            operator={operator}
            display={display}
            onDigit={pushDigit}
            onBackspace={backspace}
            onClear={clear}
            onSave={handleSave}
            onCancel={handleCancel}
            onQuick={handleQuick}
          />
        </PopoverContent>
      </Popover>
      {operation === 'subtract' && (
        <Button
          className="text-base rounded-l-none"
          wrapperClass="flex-none"
          text={`${operator}1m`}
          colour="dark"
          toolTip={`${operationVerb} by 1 minute`}
          loading={loading}
          disabled={isDisabled}
          onClick={() => onTimeOperation(defaultDuration)}
        />
      )}
    </div>
  )
}

type CalculatorProps = {
  label: string
  operator: string
  display: string
  onDigit: (d: string) => void
  onBackspace: () => void
  onClear: () => void
  onSave: () => void
  onCancel: () => void
  onQuick: (ms: number) => void
}

function TimeCalculator({
  label,
  operator,
  display,
  onDigit,
  onBackspace,
  onClear,
  onSave,
  onCancel,
  onQuick,
}: CalculatorProps) {
  // Window listener: lets the numpad accept hardware keyboard input while the
  // popover is mounted. Scoped to mount/unmount of this component, which
  // matches the popover open/close lifecycle. We handle Enter/Escape here too
  // because nothing inside the popover is focused (PopoverContent uses
  // initialFocus={-1}), so its built-in onKeyUp handler never fires.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (event.key >= '0' && event.key <= '9') {
        event.preventDefault()
        onDigit(event.key)
      } else if (event.key === 'Backspace') {
        event.preventDefault()
        onBackspace()
      } else if (event.key === 'Delete' || event.key.toLowerCase() === 'c') {
        event.preventDefault()
        onClear()
      } else if (event.key === 'Enter') {
        event.preventDefault()
        onSave()
      } else if (event.key === 'Escape') {
        event.preventDefault()
        onCancel()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onDigit, onBackspace, onClear, onSave, onCancel])

  return (
    <div className="w-56 select-none">
      <div className="flex gap-1">
        {QUICK_PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={() => onQuick(preset.ms)}
            className={cn(
              'flex-1 h-7 rounded-sm bg-gray-900 text-white text-xs font-medium',
              'hover:bg-gray-800 active:bg-gray-700',
              'focus:outline-hidden focus:ring-2 focus:ring-white/40',
            )}
          >
            {operator}
            {preset.label}
          </button>
        ))}
      </div>
      <hr className="my-2.5 border-white/10" />
      <div className="mb-2">
        <div className="text-xs text-gray-400">{label}</div>
        <div className="flex items-baseline justify-end gap-1 rounded-sm bg-black/50 px-2 py-1.5 font-mono text-2xl tabular-nums text-white">
          <span className="text-gray-400">{operator}</span>
          <span>{display}</span>
        </div>
        <div className="mt-0.5 text-right text-[10px] text-gray-500">
          hh:mm:ss
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <CalcButton key={d} onClick={() => onDigit(d)}>
            {d}
          </CalcButton>
        ))}
        <CalcButton onClick={onClear} aria-label="Clear">
          C
        </CalcButton>
        <CalcButton onClick={() => onDigit('0')}>0</CalcButton>
        <CalcButton onClick={onBackspace} aria-label="Backspace">
          <FontAwesomeIcon icon={faDeleteLeft} size="sm" />
        </CalcButton>
      </div>
    </div>
  )
}

type CalcButtonProps = {
  children: ReactNode
  onClick: () => void
  'aria-label'?: string
}

function CalcButton({
  children,
  onClick,
  'aria-label': ariaLabel,
}: CalcButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        'h-9 rounded-sm bg-gray-900 text-white text-base font-medium',
        'hover:bg-gray-800 active:bg-gray-700',
        'focus:outline-hidden focus:ring-2 focus:ring-white/40',
      )}
    >
      {children}
    </button>
  )
}
